import {
  Button,
  Callout,
  type CalloutVariant,
  colors,
  DropdownItem,
  DropdownMenu,
  EmptyState,
  FloatingActionBar,
  Icon,
  IconButton,
  Input,
  SkeletonTable,
  SkeletonText,
  Tooltip,
} from '@dev-spendesk/grapes';
import { useCallback, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';

import { QuerySuspense } from 'common/components/QuerySuspense';
import { useFeature } from 'common/hooks/useFeature';
import { useModal } from 'common/hooks/useModalGrapes';
import { useTranslation } from 'common/hooks/useTranslation';
import { formatList } from 'common/utils/intl';
import { useIsAnalyticalSplitActivated } from 'modules/bookkeep/apis/analytical-split/useAnalyticalSplitStatus';
import { RulesPromoBanner } from 'modules/bookkeep/settings/integrations/pages/PayableRulesSetupPage/components/RulesPromoBanner';
import { useCostCentersQuery } from 'modules/budgets/apis';
import type { CostCenter } from 'modules/budgets/models/costCenter';
import { CustomFieldsPageHeader } from 'modules/custom-fields/components/CustomFieldsPageHeader';
import { type CustomField } from 'modules/custom-fields/models/customField';
import { useQueryStates } from 'src/core/api/hooks/useQueryStates';
import { useCustomFieldValuesSearch } from 'src/core/common/components/CustomFieldFormField/CustomFieldAsyncAutocomplete/useCustomFieldValuesSearch';
import { useDebouncedState } from 'src/core/common/hooks/useDebounceState';
import FEATURES from 'src/core/constants/features';
import { useAccountingSoftwareName } from 'src/core/modules/accounting-integration/apis';
import { AnalyticalFieldMappingModalContainer } from 'src/core/modules/accounting-integration/components/AnalyticalFieldMapping/AnalyticalFieldMappingModalContainer';
import { CurrentMapping } from 'src/core/modules/accounting-integration/components/CurrentMapping';
import { useAnalyticalFieldMapping } from 'src/core/modules/accounting-integration/hooks/useAnalyticalFieldMapping';

import { ExpenseCategoryTable } from './ExpenseCategoryTable';
import { useExpenseCategoriesQuery } from './hooks/useExpenseCategoriesQuery';
import { ReviewSyncSettingsWarning } from '../../../components/ReviewSyncSettingsWarning';
import { EcBulkDeleteModal } from '../../components/EcBulkDeleteModal';
import { listCostCentersWithoutExpenseCategories } from '../../components/EcFormModal/listCostCentersWithoutExpenseCategories';
import { type ExpenseCategory } from '../../expenseCategory';
import { EcBulkAssignModalContainer } from '../EcBulkAssignModalContainer';
import { EcDeletionModalContainer } from '../EcDeletionModalContainer';
import {
  EcCreationFormModalContainer,
  EcEditFormModalContainer,
} from '../EcFormModalContainer';
import { ExpenseCategoriesEditSettingsContainer } from '../ExpenseCategoriesEditSettingsContainer';

type Props = {
  expenseCategoryCustomField?: CustomField;
};

export const EcContainer = ({ expenseCategoryCustomField }: Props) => {
  const { t, activeLanguage } = useTranslation('global');
  const { accountingSoftwareName } = useAccountingSoftwareName();
  const isAnalyticalSplitActivated = useIsAnalyticalSplitActivated();
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch, { isDebouncing }] =
    useDebouncedState('', 300);
  const [selectedExpenseCategories, setSelectedExpenseCategories] = useState<
    ExpenseCategory[]
  >([]);
  const expenseCategoriesQuery = useExpenseCategoriesQuery();

  // Async search using the expense category custom field
  const expenseCategoryCustomFieldId = expenseCategoryCustomField?.id;
  const { mutateAsync: searchValues, isLoading: isSearchLoading } =
    useCustomFieldValuesSearch(expenseCategoryCustomFieldId ?? '');
  const [searchResultIds, setSearchResultIds] = useState<string[] | null>(null);

  useMemo(() => {
    const setAsyncSearchResultIds = async () => {
      if (expenseCategoryCustomFieldId && debouncedSearch) {
        const results = await searchValues(debouncedSearch);
        setSearchResultIds(results.map((r) => r.key));
      } else {
        setSearchResultIds(null);
      }
    };
    setAsyncSearchResultIds();
  }, [debouncedSearch, expenseCategoryCustomFieldId]);

  const mapping = useAnalyticalFieldMapping({
    fieldType: 'expenseCategory',
    field: expenseCategoryCustomField,
    values:
      expenseCategoriesQuery.status === 'success'
        ? expenseCategoriesQuery.data
        : [],
    onUpdated: () => {
      hideSyncSettingsModal();
    },
    onDeleteValue: (value) => {
      showDeletionModal({
        expenseCategory: value,
      });
    },
  });

  const expenseCategorySyncSetting = mapping.allSyncSettings.find(
    (setting) => setting.fieldId === expenseCategoryCustomField?.id,
  );

  const isCostCentersFeatureEnabled = useFeature(
    FEATURES.COST_CENTERS_ACTIVATED,
  );
  const isBulkActionsFeatureEnabled = useFeature(
    FEATURES.TMP_EXPENSE_CATEGORIES_BULK_ACTIONS,
  );
  const queryStates = useQueryStates({
    states: {
      expenseCategories: expenseCategoriesQuery,
      costCenters: useCostCentersQuery(),
    },
    reshapeData: ({ expenseCategories, costCenters }) => ({
      expenseCategories,
      costCenters,
    }),
  });

  const renderExpenseCategoriesCallout = (
    expenseCategories: ExpenseCategory[],
    costCenters: CostCenter[],
  ) => {
    const standaloneCostCenters = listCostCentersWithoutExpenseCategories(
      costCenters,
      expenseCategories,
    );

    if (expenseCategories.length === 0 || standaloneCostCenters.length === 0) {
      return null;
    }

    const isExpenseCategoryOptional = !expenseCategoryCustomField?.isRequired;

    return renderExpenseCategoriesCalloutContent(standaloneCostCenters, {
      title: t('expenseCategories.standaloneCostCenters.title'),
      description: isExpenseCategoryOptional
        ? t('expenseCategories.standaloneCostCenters.description.optionalEcs')
        : t('expenseCategories.standaloneCostCenters.description.mandatoryEcs'),
      variant: 'info',
    });
  };

  const renderExpenseCategoriesCalloutContent = (
    standaloneCostCenters: CostCenter[],
    content: { title: string; variant: CalloutVariant; description: string },
  ) => {
    const { title, variant, description } = content;

    return (
      <Callout className="mx-16 mb-16" title={title} variant={variant}>
        <div>
          <Trans i18nKey={description} />
          <div className="mt-16">
            {standaloneCostCenters.map(({ name }) => name).join(', ')}
          </div>
        </div>
      </Callout>
    );
  };

  const [createExpenseCategoryModal, showCreateExpenseCategoryModal] =
    useModal<{ expenseCategory: ExpenseCategory }>(({ isOpen, onClose }) => (
      <EcCreationFormModalContainer
        isOpen={isOpen}
        onComplete={onClose}
        onCancel={onClose}
        isMapSyncEnabled={mapping.isMapSyncEnabled}
        isSplittable={!!expenseCategoryCustomField?.isSplittable}
      />
    ));

  const [editExpenseCategoryModal, showEditExpenseCategoryModal] = useModal<{
    expenseCategory: ExpenseCategory;
  }>(
    ({ isOpen, onClose, expenseCategory }) =>
      expenseCategory && (
        <EcEditFormModalContainer
          expenseCategory={expenseCategory}
          isSplittable={!!expenseCategoryCustomField?.isSplittable}
          isOpen={isOpen}
          onComplete={onClose}
          onCancel={onClose}
        />
      ),
  );

  const [deletionModal, showDeletionModal] = useModal<{
    expenseCategory: ExpenseCategory;
  }>(({ isOpen, onClose, expenseCategory }) => {
    if (expenseCategory === undefined) {
      return <div />;
    }

    return (
      <EcDeletionModalContainer
        expenseCategory={expenseCategory}
        isOpen={isOpen}
        onComplete={onClose}
        onClose={onClose}
        isMapSyncEnabled={mapping.isMapSyncEnabled}
        saveMappingChanges={mapping.onSaveChanges}
        fieldMapping={mapping.fieldMapping}
        syncMode={mapping.syncMode}
      />
    );
  });

  const [
    expenseCategoriesEditSettingsModal,
    showExpenseCategoriesEditSettingsModal,
  ] = useModal(({ isOpen, onClose }) => {
    if (expenseCategoryCustomField === undefined) {
      return <></>;
    }

    return (
      <ExpenseCategoriesEditSettingsContainer
        isOpen={isOpen}
        expenseCategoryCustomField={expenseCategoryCustomField}
        onClose={onClose}
        onComplete={onClose}
      />
    );
  });

  const [bulkAssignModal, showBulkAssignModal] = useModal<{
    expenseCategories: ExpenseCategory[];
  }>(({ isOpen, onClose, expenseCategories }) => {
    if (!expenseCategories) {
      return <></>;
    }
    return (
      <EcBulkAssignModalContainer
        expenseCategories={expenseCategories}
        isOpen={isOpen}
        onComplete={() => {
          setSelectedExpenseCategories([]);
          onClose();
        }}
        onCancel={onClose}
      />
    );
  });

  const [bulkDeleteModal, showBulkDeleteModal] = useModal<{
    expenseCategories: ExpenseCategory[];
  }>(({ isOpen, onClose, expenseCategories }) => {
    if (!expenseCategories) {
      return <></>;
    }
    return (
      <EcBulkDeleteModal
        expenseCategories={expenseCategories}
        isOpen={isOpen}
        onComplete={() => {
          setSelectedExpenseCategories([]);
          onClose();
        }}
        onClose={onClose}
      />
    );
  });

  const [syncSettingsModal, showSyncSettingsModal, hideSyncSettingsModal] =
    useModal(({ isOpen }) => {
      if (!mapping.isMapSyncEnabled || !expenseCategoryCustomField) {
        return <></>;
      }

      if (queryStates.status !== 'success') {
        return <></>;
      }

      return (
        <AnalyticalFieldMappingModalContainer
          isOpen={isOpen}
          onClose={() => hideSyncSettingsModal()}
          internalField={expenseCategoryCustomField}
          isSplittable={!!expenseCategoryCustomField.isSplittable}
          values={queryStates.data.expenseCategories}
          fieldType="expenseCategory"
          mapping={mapping}
        />
      );
    });

  const optionMap = useMemo(
    () =>
      new Map<'expenseCategorySettings' | 'syncSettings', () => void>([
        ['expenseCategorySettings', showExpenseCategoriesEditSettingsModal],
        ['syncSettings', showSyncSettingsModal],
      ]),
    [showCreateExpenseCategoryModal, showSyncSettingsModal],
  );

  const handleMenuItemSelected = useCallback(
    (option: 'expenseCategorySettings' | 'syncSettings') => {
      optionMap.get(option)?.();
    },
    [optionMap],
  );

  // Prepare header tags outside suspense so header stays visible during loading
  const headerTags: string[] = [];
  if (
    expenseCategoryCustomField?.type === 'list' &&
    isAnalyticalSplitActivated
  ) {
    headerTags.push(
      expenseCategoryCustomField.isSplittable
        ? t('customFields.settings.splittable')
        : t('customFields.settings.nonSplittable'),
    );
  }
  if (!expenseCategoryCustomField?.isRequired) {
    headerTags.push(t('misc.optional').toLocaleLowerCase());
  }
  const headerFormattedTags = formatList(activeLanguage, headerTags, t);

  return (
    <>
      <div className="w-full max-w-lg self-center">
        <CustomFieldsPageHeader
          title={
            <>
              {t('expenseCategories.setup.title')}{' '}
              {isAnalyticalSplitActivated ? (
                <span className="text-content-primary body-l">
                  ({headerFormattedTags})
                </span>
              ) : null}
            </>
          }
          subtitle={t('expenseCategories.expenseCategoriesDescription')}
          className="mb-24"
          rightAddon={
            <div className="flex items-center gap-8">
              <Input
                type="search"
                className="w-full"
                placeholder={t('misc.search')}
                value={searchInput}
                fit="content"
                leftAddon={
                  <Icon
                    name="magnifying-glass"
                    color={colors.contentDecorativeIcon}
                    className="ml-8"
                    size="m"
                  />
                }
                onChange={(event) => {
                  const value = event.target.value;
                  setSearchInput(value);
                  setDebouncedSearch(value);
                }}
              />

              {expenseCategorySyncSetting?.syncMode === 'pull' ? (
                <Tooltip
                  content={t(
                    'expenseCategories.mapSync.addExpenseCategoryDisabledTooltip',
                    { integration: accountingSoftwareName },
                  )}
                >
                  <Button
                    isDisabled
                    className="shrink-0 whitespace-nowrap"
                    variant="secondaryNeutral"
                    text={t('expenseCategories.addExpenseCategory')}
                    onClick={() => showCreateExpenseCategoryModal()}
                  />
                </Tooltip>
              ) : (
                <Button
                  className="shrink-0 whitespace-nowrap"
                  variant="secondaryNeutral"
                  text={t('expenseCategories.addExpenseCategory')}
                  onClick={() => showCreateExpenseCategoryModal()}
                />
              )}

              {!mapping.isMapSyncEnabled && expenseCategoryCustomField && (
                <IconButton
                  iconName="gear"
                  variant="secondaryNeutral"
                  onClick={() => showExpenseCategoriesEditSettingsModal()}
                  aria-label={t('misc.edit')}
                />
              )}
              {mapping.isMapSyncEnabled && (
                <DropdownMenu
                  options={[
                    {
                      key: 'expenseCategorySettings' as const,
                      label: t('expenseCategories.editSettings'),
                    },
                    {
                      key: 'syncSettings' as const,
                      label: t('expenseCategories.editSyncSettings'),
                    },
                  ]}
                  renderButton={(getProps) => (
                    <IconButton
                      {...getProps()}
                      iconName="gear"
                      aria-label={t('misc.edit')}
                      variant="secondaryNeutral"
                    />
                  )}
                  renderOption={(option) => (
                    <DropdownItem className="w-[256px]" label={option.label} />
                  )}
                  placement="bottom-end"
                  onSelect={(option) => handleMenuItemSelected(option.key)}
                />
              )}
            </div>
          }
        />
      </div>
      <QuerySuspense
        queryState={queryStates}
        fallback={() => (
          <EmptyState
            title={t('misc.errors.networkError')}
            subtitle=""
            iconVariant="alert"
            iconName="hexagone-cross"
          />
        )}
        loading={
          <div className="w-full max-w-lg self-center">
            <SkeletonTable
              columns={[
                {
                  cell: <SkeletonText width="80%" />,
                  header: <SkeletonText width="100%" />,
                  width: '33%',
                },
                {
                  cell: <SkeletonText width="80%" />,
                  header: <SkeletonText width="100%" />,
                  width: '34%',
                },
                {
                  cell: <SkeletonText width="80%" />,
                  header: <SkeletonText width="100%" />,
                  width: '33%',
                },
              ]}
              withHeader
              numberOfRows={5}
              rowHeight="compact"
            />
          </div>
        }
      >
        {({ costCenters, expenseCategories }) => {
          return (
            <div className="w-full max-w-lg self-center">
              {renderExpenseCategoriesCallout(expenseCategories, costCenters)}

              {mapping.showWarningCallout && (
                <ReviewSyncSettingsWarning
                  onClick={() => showSyncSettingsModal()}
                />
              )}

              {mapping.isMapSyncEnabled && (
                <CurrentMapping
                  isLoading={mapping.isIntegrationNameLoading}
                  fieldName={mapping.fieldMapping?.externalName}
                  syncMode={mapping.syncMode}
                  isWrapped
                />
              )}

              {isDebouncing || (debouncedSearch && isSearchLoading) ? (
                <div className="w-full">
                  <SkeletonTable
                    columns={[
                      {
                        cell: <SkeletonText width="50%" />,
                        header: <SkeletonText width="40%" />,
                        width: '50%',
                      },
                      {
                        cell: <SkeletonText width="40%" />,
                        header: <SkeletonText width="30%" />,
                        width: '50%',
                      },
                    ]}
                    withHeader
                    numberOfRows={5}
                    rowHeight="compact"
                  />
                </div>
              ) : (
                <>
                  {selectedExpenseCategories.length > 0 &&
                    isBulkActionsFeatureEnabled && (
                      <FloatingActionBar
                        className="mb-64"
                        actions={[
                          {
                            text: t('expenseCategories.bulkAssign.button'),
                            onClick: () => {
                              showBulkAssignModal({
                                expenseCategories: selectedExpenseCategories,
                              });
                            },
                            iconName: 'tag',
                          },
                          {
                            text: t('expenseCategories.bulkDelete.button'),
                            onClick: () => {
                              showBulkDeleteModal({
                                expenseCategories: selectedExpenseCategories,
                              });
                            },
                            iconName: 'trash',
                          },
                        ]}
                      >
                        {t('expenseCategories.selectedCategories', {
                          count: selectedExpenseCategories.length,
                        })}
                      </FloatingActionBar>
                    )}
                  <ExpenseCategoryTable
                    costCenters={costCenters}
                    expenseCategories={
                      debouncedSearch &&
                      expenseCategoryCustomFieldId &&
                      searchResultIds
                        ? mapping.sortedValues.filter((ec) =>
                            searchResultIds.includes(ec.id),
                          )
                        : mapping.sortedValues.filter((ec) =>
                            debouncedSearch
                              ? ec.name
                                  .toLowerCase()
                                  .includes(debouncedSearch.toLowerCase())
                              : true,
                          )
                    }
                    isCostCentersFeatureEnabled={isCostCentersFeatureEnabled}
                    maxHeight={330}
                    onDelete={(expenseCategory: ExpenseCategory) =>
                      showDeletionModal({ expenseCategory })
                    }
                    onEdit={(expenseCategory: ExpenseCategory) =>
                      showEditExpenseCategoryModal({ expenseCategory })
                    }
                    externalValues={mapping.externalValues}
                    mapping={mapping.fieldMapping}
                    selectedExpenseCategories={
                      isBulkActionsFeatureEnabled
                        ? selectedExpenseCategories
                        : undefined
                    }
                    onSelectionChange={
                      isBulkActionsFeatureEnabled
                        ? setSelectedExpenseCategories
                        : undefined
                    }
                    syncMode={mapping.syncMode}
                  />
                </>
              )}
              {createExpenseCategoryModal}
              {editExpenseCategoryModal}
              {deletionModal}
              {expenseCategoriesEditSettingsModal}
              {syncSettingsModal}
              {bulkAssignModal}
              {bulkDeleteModal}
              <RulesPromoBanner className="mt-16" />
            </div>
          );
        }}
      </QuerySuspense>
    </>
  );
};
